import axios, { AxiosError } from 'axios';
import { auth } from '../firebase'; // your firebase.ts file


interface ApiError {
  message?: string;
  error?: string;
}

interface Business {
  id: number;
  name: string;
  registryId: string;
  status: string;
  location: string;
  businessId: number;
  userId: number;
  createdAt: string;
  updatedAt: string;
  templateId?: number | null;
  logo?: string | null;
  details?: {
    description?: string;
    investment_level?: string;
    sector?: string;
    scalability?: string;
    economic_activity?: string;
    business_model?: string;
  };
}

export const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

export const connect_tool = async (businessId: number, tool: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    
    const response = await backendAPI.post(
      'tools/connect/',
      { businessId, tool },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        }
      }    
    );
    return response;
  } catch (error: any) {
    throw new Error((error as AxiosError<ApiError>)?.response?.data?.message || 'Tool connection failed');
  }
};
  
export const check_tool = async (businessId: number, tool: string) => {
  try {
    const headers: Record<string, string> = {};
    const user = auth.currentUser;
    if (user) {
      headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }

    const response = await backendAPI.get(`tools/${businessId}/${tool}`, { headers });
    return response.data; 
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 
      'Tool check failed'
    );
  }
};

export type MercadoPagoConnectionStatus =
  | "not_connected"
  | "connected"
  | "expired"
  | "reconnect_required";

export const get_mercadopago_status = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/mercadopago/status", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: { businessId },
    });

    return response.data as {
      status: MercadoPagoConnectionStatus;
      accountId: string | null;
      publicKey: string | null;
      tokenExpiry: string | null;
      updatedAt: string | null;
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to fetch MercadoPago status"
    );
  }
};

export const disconnect_mercadopago = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/mercadopago/disconnect",
      { businessId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as { disconnected: boolean };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to disconnect MercadoPago"
    );
  }
};

export type LinkedInConnectionStatus =
  | "not_connected"
  | "connected"
  | "expired"
  | "reconnect_required";

export const get_linkedin_status = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/linkedin/status", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: { businessId },
    });

    return response.data as {
      status: LinkedInConnectionStatus;
      accountId: string | null;
      memberName: string | null;
      authorUrn: string | null;
      tokenExpiry: string | null;
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to fetch LinkedIn status"
    );
  }
};

export const disconnect_mailchimp = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/mailchimp/disconnect",
      { businessId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as { disconnected: boolean; status: string };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to disconnect Mailchimp"
    );
  }
};

export type InstagramAsset = {
  pageId: string;
  pageName: string | null;
  instagramId: string;
  username: string | null;
  name: string | null;
};

export type InstagramConnectionStatus =
  | "not_connected"
  | "connected"
  | "selection_required"
  | "reconnect_required";

export const get_instagram_status = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/instagram/status", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: { businessId },
    });

    return response.data as {
      status: InstagramConnectionStatus;
      accountId: string | null;
      username: string | null;
      name: string | null;
      pageId: string | null;
      pageName: string | null;
      tokenExpiry: string | null;
      assets: InstagramAsset[];
      grantedPermissions: string[];
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to fetch Instagram status"
    );
  }
};

export const select_instagram_account = async (businessId: number, instagramId: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/instagram/select",
      { businessId, instagramId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as {
      status: InstagramConnectionStatus;
      accountId: string | null;
      username: string | null;
      pageName: string | null;
      assets: InstagramAsset[];
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to select Instagram account"
    );
  }
};

export type MetaAdAccount = {
  id: string;
  accountId: string | null;
  name: string | null;
  currency: string | null;
  accountStatus: number | null;
};

export type MetaAdsConnectionStatus =
  | "not_connected"
  | "connected"
  | "selection_required"
  | "reconnect_required";

export type MetaAdsStatusResponse = {
  status: MetaAdsConnectionStatus;
  accountId: string | null;
  name: string | null;
  currency: string | null;
  accountStatus: number | null;
  accountStatusLabel: string | null;
  tokenExpiry: string | null;
  assets: MetaAdAccount[];
  grantedPermissions: string[];
};

export const get_meta_ads_status = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/meta-ads/status", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: { businessId },
    });

    return response.data as MetaAdsStatusResponse;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to fetch Meta Ads status"
    );
  }
};

export const select_meta_ad_account = async (businessId: number, adAccountId: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/meta-ads/select",
      { businessId, adAccountId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as MetaAdsStatusResponse;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to select Meta ad account"
    );
  }
};

export type WhatsAppLaunchConfig = {
  appId: string;
  configId: string;
  graphVersion: string;
  version: "v4";
  responseType: "code";
  overrideDefaultResponseType: boolean;
  extras: { setup: Record<string, never> };
  products: string[];
  permissions: string[];
};

export type WhatsAppStatus = {
  status: "not_connected" | "connected" | "reconnect_required";
  phoneNumberId: string | null;
  wabaId: string | null;
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  tokenExpiry: string | null;
  grantedPermissions: string[];
  messagingReady: boolean;
  webhookSubscribed: boolean;
  phoneRegistration: "not_configured" | "connected" | "unavailable";
};

export const get_whatsapp_config = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/whatsapp/config", {
      headers: { Authorization: `Bearer ${idToken}` },
      params: { businessId },
    });
    return response.data as WhatsAppLaunchConfig;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to load WhatsApp configuration"
    );
  }
};

export const get_whatsapp_status = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/whatsapp/status", {
      headers: { Authorization: `Bearer ${idToken}` },
      params: { businessId },
    });
    return response.data as WhatsAppStatus;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to fetch WhatsApp status"
    );
  }
};

export const complete_whatsapp_connection = async (
  businessId: number,
  payload: { code: string; wabaId: string; phoneNumberId: string; event: string }
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/whatsapp/complete",
      { businessId, ...payload },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data as WhatsAppStatus;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to connect WhatsApp"
    );
  }
};

export type WhatsAppConversationSummary = {
  id: string;
  phoneNumberId: string;
  waId: string;
  unreadCount: number;
  latestMessage: {
    messageType: string | null;
    preview: string | null;
    at: string;
    direction: "inbound" | "outbound";
  } | null;
};

export type WhatsAppInboxMessage = {
  id: string;
  messageType: string | null;
  textBody: string | null;
  mediaMetadata: { caption?: string | null } | null;
  providerMessageId: string | null;
  at: string;
  direction: "inbound" | "outbound";
  sendState: string | null;
  errorClass: string | null;
  clientRequestId: string | null;
};

export const get_whatsapp_conversations = async (businessId: number, cursor?: string | null) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/whatsapp/conversations", {
      headers: { Authorization: `Bearer ${idToken}` },
      params: { businessId, cursor: cursor || undefined },
    });
    return response.data as { conversations: WhatsAppConversationSummary[]; nextCursor: string | null };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to load WhatsApp conversations"
    );
  }
};

export const get_whatsapp_messages = async (
  businessId: number,
  conversationId: string,
  cursor?: string | null
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.get(`tools/whatsapp/conversations/${conversationId}/messages`, {
      headers: { Authorization: `Bearer ${idToken}` },
      params: { businessId, cursor: cursor || undefined },
    });
    return response.data as {
      conversation: { id: string; phoneNumberId: string; waId: string; unreadCount: number };
      messages: WhatsAppInboxMessage[];
      nextCursor: string | null;
      messagingWindow: "open" | "closed";
      latestInboundMessageId: string | null;
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to load WhatsApp messages"
    );
  }
};

export const mark_whatsapp_conversation_read = async (businessId: number, conversationId: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      `tools/whatsapp/conversations/${conversationId}/read`,
      { businessId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data as { id: string; unreadCount: number };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to update the WhatsApp conversation"
    );
  }
};

export const check_whatsapp_messaging = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/whatsapp/messaging-check",
      { businessId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data as WhatsAppStatus;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to check WhatsApp messaging"
    );
  }
};

export const send_whatsapp_message = async (
  businessId: number,
  conversationId: string,
  payload: {
    text: string;
    clientRequestId: string;
    retry?: boolean;
    messageOrigin?: "manual" | "suggestion";
    suggestionContextMessageId?: string | null;
  }
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      `tools/whatsapp/conversations/${conversationId}/messages`,
      {
        businessId,
        text: payload.text,
        clientRequestId: payload.clientRequestId,
        retry: payload.retry === true,
        messageOrigin: payload.messageOrigin === "suggestion" ? "suggestion" : "manual",
        suggestionContextMessageId: payload.suggestionContextMessageId || null,
      },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data as { message: WhatsAppInboxMessage };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to send the WhatsApp message"
    );
  }
};

export type WhatsAppReplyDraft = {
  draft: string | null;
  caution: string | null;
  explanation: string | null;
  contextMessageId: string | null;
  messagingWindow: "open" | "closed";
  needsOwner: boolean;
};

export const suggest_whatsapp_reply = async (businessId: number, conversationId: string) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      `tools/whatsapp/conversations/${conversationId}/draft`,
      { businessId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data as WhatsAppReplyDraft;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to suggest a WhatsApp reply"
    );
  }
};

export const subscribe_whatsapp = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/whatsapp/subscribe",
      { businessId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data as WhatsAppStatus;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to enable WhatsApp message reception"
    );
  }
};

export const disconnect_whatsapp = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");
    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/whatsapp/disconnect",
      { businessId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data as { disconnected: boolean; remoteRevocation: false };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to disconnect WhatsApp"
    );
  }
};

export const disconnect_meta_ads = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/meta-ads/disconnect",
      { businessId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as { disconnected: boolean; remoteRevocation: false };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to disconnect Meta Ads"
    );
  }
};

export const disconnect_instagram = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/instagram/disconnect",
      { businessId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as { disconnected: boolean };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to disconnect Instagram"
    );
  }
};

export type InstagramPublishResult = {
  campaignId: number;
  status: "processing" | "published" | "uncertain" | "failed";
  permalink: string | null;
  errorCode: string | null;
  message: string;
};

export const publish_instagram_post = async (
  businessId: number,
  imageBase64: string,
  caption: string,
  idempotencyKey: string
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/instagram/publish",
      { businessId, imageBase64, caption, idempotencyKey },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as InstagramPublishResult;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to publish Instagram post"
    );
  }
};

export const get_instagram_post = async (businessId: number, campaignId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get(`tools/instagram/posts/${campaignId}`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: { businessId },
    });

    return response.data as InstagramPublishResult;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to fetch Instagram post"
    );
  }
};

export type InstagramInsightMetric = {
  metric: string;
  label: string;
  definition: string;
  displayNote: string;
  period: string;
  aggregation: string | null;
  additive: boolean;
  value: number | null;
  unavailable: boolean;
  availability: string;
  periodStart: string | null;
  periodEnd: string | null;
  retrievedAt: string | null;
};

export type InstagramInsightsSnapshot = {
  status: "not_connected" | "reconnect_required" | "missing_permissions" | "empty" | "ready";
  accountId: string | null;
  username: string | null;
  lastRetrievedAt: string | null;
  stale: boolean;
  missingPermissions: string[];
  reportingPeriod: {
    windowDays: number;
    period: string;
    aggregation: string;
    additive: boolean;
    periodStart: string | null;
    periodEnd: string | null;
    note: string;
  } | null;
  accountMetrics: InstagramInsightMetric[];
  media: Array<{
    mediaId: string;
    permalink: string | null;
    caption: string | null;
    timestamp: string | null;
    productType: string | null;
    retrievedAt: string | null;
    metrics: InstagramInsightMetric[];
  }>;
  notCollected: Array<{ metric: string; reason: string }>;
  refresh?: { ok: boolean; errorCode: string | null; partial: boolean };
};

export const get_instagram_insights = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/instagram/insights", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: { businessId },
    });

    return response.data as InstagramInsightsSnapshot;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "No pudimos leer las estadísticas de Instagram."
    );
  }
};

export const refresh_instagram_insights = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/instagram/insights/refresh",
      { businessId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as InstagramInsightsSnapshot;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "No pudimos actualizar las estadísticas de Instagram."
    );
  }
};

export const disconnect_linkedin = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/linkedin/disconnect",
      { businessId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as { disconnected: boolean };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to disconnect LinkedIn"
    );
  }
};

export type ShipitConnectionStatus = "not_connected" | "connected";

export const connect_shipit = async (
  businessId: number,
  email: string,
  accessToken: string
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/logistics/shipit/connect",
      { businessId, email, accessToken },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as { connected: boolean; status: ShipitConnectionStatus };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to connect Shipit"
    );
  }
};

export const get_shipit_status = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get("tools/logistics/shipit/status", {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: { businessId },
    });

    return response.data as {
      status: ShipitConnectionStatus;
      connectorId: string;
      accountId: string | null;
      email: string | null;
      updatedAt: string | null;
    };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to fetch Shipit status"
    );
  }
};

export const disconnect_shipit = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.post(
      "tools/logistics/shipit/disconnect",
      { businessId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data as { disconnected: boolean };
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.error ||
      "Failed to disconnect Shipit"
    );
  }
};

export const get_user_business_tools = async (businessId: number) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();
    const response = await backendAPI.get('tools/userbusiness', {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      params: {
        businessId,
      },
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message || 'Failed to fetch tools'
    );
  }
};

export const get_newsletter_recommendation = async (
  business: Business,
  onToken: (token: string) => void
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("User not logged in");

    const idToken = await user.getIdToken();

    const response = await fetch(`${process.env.REACT_APP_API_URL}businesses/newsletter_recommendation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        business: {
          id:business.id,
          name: business.name,
          description: business.details?.description,
        },
      }),

    });

    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      onToken(chunk);
    }
  } catch (error: any) {
    console.error(error);
    throw new Error("Get newsletter recommendation failed");
  }
};